import React, { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  FormControlLabel,
  MenuItem,
  Stack,
  Switch,
  ToggleButton,
  ToggleButtonGroup,
  TextField,
  Typography,
} from '@mui/material';
import { Add as AddIcon, Clear as ClearIcon } from '@mui/icons-material';
import { toast } from 'react-toastify';
import AdminLayout from '../components/AdminLayout';
import api from '../services/api';

type FieldType = 'text' | 'email' | 'phone' | 'date' | 'select' | 'password' | 'textarea' | 'height' | 'time' | 'time_dropdown';

interface RegistrationField {
  field_key: string;
  label: string;
  input_type: FieldType;
  placeholder?: string;
  helper_text?: string;
  options: string[];
  depends_on?: string;
  grouped_options?: Record<string, string[]>;
  is_core: boolean;
  is_enabled: boolean;
  is_required: boolean;
  display_order: number;
}

const DEFAULT_NEW_FIELD: RegistrationField = {
  field_key: '',
  label: '',
  input_type: 'text',
  placeholder: '',
  helper_text: '',
  options: [],
  depends_on: '',
  grouped_options: {},
  is_core: false,
  is_enabled: true,
  is_required: false,
  display_order: 999,
};

const AdminRegistrationFieldsPage: React.FC = () => {
  const [configMode, setConfigMode] = useState<'registration' | 'profile'>('registration');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [rows, setRows] = useState<RegistrationField[]>([]);
  const [groupedOptionsDrafts, setGroupedOptionsDrafts] = useState<Record<string, string>>({});

  const normalizeKey = (rawValue: string) => rawValue.toLowerCase().replace(/[^a-z0-9_]/g, '_').replace(/^_+|_+$/g, '');

  const fetchConfig = async () => {
    try {
      setLoading(true);
      const endpoint = configMode === 'registration' ? '/users/admin/registration-fields' : '/users/admin/profile-fields';
      const response = await api.get(endpoint);
      const items: RegistrationField[] = (response.data.items || []).map((item: any, index: number) => ({
        ...item,
        input_type: (item.input_type || 'text') as FieldType,
        options: item.options || [],
        depends_on: item.depends_on || '',
        grouped_options: item.grouped_options || {},
        display_order: item.display_order ?? (index + 1) * 10,
      }));
      setRows(items);
      setGroupedOptionsDrafts({});
    } catch (error: any) {
      toast.error(error.response?.data?.detail || 'Failed to load form fields');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchConfig();
  }, [configMode]);

  const updateRow = (index: number, patch: Partial<RegistrationField>) => {
    setRows((prev) => prev.map((row, i) => (i === index ? { ...row, ...patch } : row)));
  };

  const groupedOptionsText = (row: RegistrationField) => Object.entries(row.grouped_options || {})
    .map(([key, values]) => `${key}: ${(values || []).join(', ')}`)
    .join('\n');

  const parseGroupedOptions = (text: string): Record<string, string[]> => {
    const grouped_options: Record<string, string[]> = {};
    text.split('\n').forEach((line) => {
      const separatorIndex = line.indexOf(':');
      if (separatorIndex < 0) return;
      const parentKey = line.slice(0, separatorIndex).trim();
      if (!parentKey) return;
      grouped_options[parentKey] = line.slice(separatorIndex + 1).split(',').map((value) => value.trim()).filter(Boolean);
    });
    return grouped_options;
  };

  const addField = () => {
    setRows((prev) => [...prev, { ...DEFAULT_NEW_FIELD, display_order: prev.length * 10 + 10 }]);
  };

  const removeField = (index: number) => {
    const target = rows[index];
    if (target.is_core) {
      toast.error('Core fields cannot be removed');
      return;
    }
    setRows((prev) => prev.filter((_, i) => i !== index));
  };

  const saveConfig = async () => {
    const invalid = rows.find((row) => {
      const generatedKey = normalizeKey(row.field_key || row.label || '');
      return !generatedKey || !row.label.trim();
    });
    if (invalid) {
      toast.error('Each field must have a key and label');
      return;
    }

    try {
      setSaving(true);
      const endpoint = configMode === 'registration' ? '/users/admin/registration-fields' : '/users/admin/profile-fields';
      const payload = {
        items: rows.map((row, index) => ({
          ...row,
          field_key: normalizeKey(row.field_key || row.label),
          label: row.label.trim(),
          display_order: (index + 1) * 10,
          options: row.input_type === 'select' ? row.options.map((option) => option.trim()).filter(Boolean) : [],
          depends_on: row.input_type === 'select' ? (row.depends_on || '') : '',
          grouped_options: row.input_type === 'select' ? parseGroupedOptions(groupedOptionsDrafts[row.field_key] ?? groupedOptionsText(row)) : {},
        })),
      };
      const response = await api.put(endpoint, payload);
      setRows(response.data.items || []);
      setGroupedOptionsDrafts({});
      toast.success(configMode === 'registration' ? 'Registration form config saved' : 'Profile form config saved');
    } catch (error: any) {
      toast.error(error.response?.data?.detail || 'Failed to save form fields');
    } finally {
      setSaving(false);
    }
  };

  return (
    <AdminLayout currentPage="registration-fields">
      <Box sx={{ mb: 3 }}>
        <Typography variant="h4" sx={{ fontWeight: 'bold', mb: 1 }}>
          Form Field Controls
        </Typography>
        <Typography variant="body2" color="textSecondary">
          Configure field visibility, dropdown options, and requirement rules for both registration and complete-profile forms.
        </Typography>
        <Typography variant="subtitle1" sx={{ mt: 1, color: '#7b0011', fontWeight: 700 }}>
          Editing: {configMode === 'registration' ? 'Registration Form' : 'Complete Profile Form'}
        </Typography>
        {configMode === 'profile' && (
          <Typography variant="body2" sx={{ mt: 0.75, color: '#6f6259' }}>
            For State → City → Pin Code mapping, enable Advanced mode. Set City Depends On = State and Pin Code Depends On = City, then add one grouped-options line per parent value, for example: Maharashtra: Mumbai, Pune.
          </Typography>
        )}
      </Box>

      <ToggleButtonGroup
        value={configMode}
        exclusive
        onChange={(_, value) => {
          if (value) setConfigMode(value);
        }}
        sx={{ mb: 2 }}
      >
        <ToggleButton value="registration">Registration Form</ToggleButton>
        <ToggleButton value="profile">Complete Profile Form</ToggleButton>
      </ToggleButtonGroup>

      <Box sx={{ mb: 1.5 }}>
        <FormControlLabel
          control={<Switch checked={showAdvanced} onChange={(e) => setShowAdvanced(e.target.checked)} />}
          label="Advanced mode (show technical keys and dependency mapping)"
        />
      </Box>

      <Alert severity="info" sx={{ mb: 2 }}>
        {configMode === 'registration'
          ? 'Custom fields are validated at signup. Core identity fields remain required for account integrity.'
          : 'These settings control the Complete Profile form shown to users. Select a field type, edit its options, then click Save Configuration.'}
      </Alert>
      {configMode === 'profile' && (
        <Alert severity="info" sx={{ mb: 2 }}>
          Permanent Pin Code belongs to Permanent Address. Current Pin Code belongs to Current Address. Use City as the parent for each respective pincode mapping.
        </Alert>
      )}

      {loading ? (
        <Box sx={{ py: 8, display: 'flex', justifyContent: 'center' }}>
          <CircularProgress />
        </Box>
      ) : (
        <Stack spacing={2}>
          {rows.map((row, index) => (
            <Card key={`${row.field_key}-${index}`}>
              <CardContent>
                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: showAdvanced ? '1fr 1fr 180px 160px 160px' : '1.2fr 180px 160px 160px' }, gap: 1.25 }}>
                  {showAdvanced && (
                    <TextField
                      size="small"
                      label="Field Key"
                      value={row.field_key}
                      disabled={row.is_core}
                      onChange={(e) => updateRow(index, { field_key: normalizeKey(e.target.value) })}
                    />
                  )}
                  <TextField
                    size="small"
                    label="Label"
                    value={row.label}
                    onChange={(e) => {
                      const nextLabel = e.target.value;
                      const patch: Partial<RegistrationField> = { label: nextLabel };
                      if (!showAdvanced && !row.is_core && !row.field_key.trim()) {
                        patch.field_key = normalizeKey(nextLabel);
                      }
                      updateRow(index, patch);
                    }}
                  />
                  <TextField
                    size="small"
                    select
                    label="Input Type"
                    value={row.input_type}
                    onChange={(e) => updateRow(index, { input_type: e.target.value as FieldType })}
                  >
                    <MenuItem value="text">text</MenuItem>
                    <MenuItem value="email">email</MenuItem>
                    <MenuItem value="phone">phone</MenuItem>
                    <MenuItem value="date">date</MenuItem>
                    <MenuItem value="select">select</MenuItem>
                    <MenuItem value="password">password</MenuItem>
                    <MenuItem value="textarea">textarea</MenuItem>
                    <MenuItem value="height">height (feet/inches picker)</MenuItem>
                    <MenuItem value="time">time (clock picker)</MenuItem>
                    <MenuItem value="time_dropdown">time dropdown (HH/MM)</MenuItem>
                  </TextField>
                  <TextField
                    size="small"
                    select
                    label="Enabled"
                    value={row.is_enabled ? 'YES' : 'NO'}
                    disabled={row.is_core}
                    onChange={(e) => updateRow(index, { is_enabled: e.target.value === 'YES' })}
                  >
                    <MenuItem value="YES">Yes</MenuItem>
                    <MenuItem value="NO">No</MenuItem>
                  </TextField>
                  <TextField
                    size="small"
                    select
                    label="Required"
                    value={row.is_required ? 'YES' : 'NO'}
                    disabled={row.is_core}
                    onChange={(e) => updateRow(index, { is_required: e.target.value === 'YES' })}
                  >
                    <MenuItem value="YES">Yes</MenuItem>
                    <MenuItem value="NO">No</MenuItem>
                  </TextField>
                </Box>

                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr auto' }, gap: 1.25, mt: 1.5 }}>
                  <TextField
                    size="small"
                    label="Placeholder"
                    value={row.placeholder || ''}
                    onChange={(e) => updateRow(index, { placeholder: e.target.value })}
                  />
                  <TextField
                    size="small"
                    label="Helper Text"
                    value={row.helper_text || ''}
                    onChange={(e) => updateRow(index, { helper_text: e.target.value })}
                  />
                  <Stack direction="row" spacing={1} alignItems="center" justifyContent="flex-end">
                    {row.is_core && <Chip size="small" color="primary" label="Core" />}
                    {!showAdvanced && !row.is_core && row.field_key && (
                      <Chip size="small" variant="outlined" label={`Key: ${row.field_key}`} />
                    )}
                    <Button color="error" variant="outlined" onClick={() => removeField(index)} disabled={row.is_core}>
                      Remove
                    </Button>
                  </Stack>
                </Box>

                {row.input_type === 'select' && (
                  <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: showAdvanced ? '1fr 220px' : '1fr' }, gap: 1.25, mt: 1.5 }}>
                    <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
                      <TextField
                        size="small"
                        fullWidth
                        label="Options (comma separated)"
                        helperText="Type options separated by commas. Delete text and commas to remove options."
                        value={(row.options || []).join(', ')}
                        onChange={(e) => updateRow(index, { options: e.target.value.split(',').map((value) => value.trim()) })}
                      />
                      <Button
                        aria-label="Clear options"
                        title="Clear all options"
                        variant="outlined"
                        color="error"
                        onClick={() => updateRow(index, { options: [] })}
                        sx={{ minWidth: 42, height: 40, px: 1 }}
                      >
                        <ClearIcon fontSize="small" />
                      </Button>
                    </Box>
                    {showAdvanced && (
                      <TextField
                        size="small"
                        select
                        fullWidth
                        label="Depends On"
                        value={row.depends_on || ''}
                        onChange={(e) => updateRow(index, { depends_on: e.target.value })}
                      >
                        <MenuItem value="">None</MenuItem>
                        {rows
                          .filter((candidate, candidateIndex) => candidateIndex !== index && candidate.input_type === 'select' && candidate.field_key)
                          .map((candidate) => (
                            <MenuItem key={`depends-${candidate.field_key}`} value={candidate.field_key}>
                              {candidate.label} ({candidate.field_key})
                            </MenuItem>
                          ))}
                      </TextField>
                    )}
                    {showAdvanced && (
                      <TextField
                        size="small"
                        fullWidth
                        multiline
                        minRows={row.depends_on ? 4 : 3}
                        sx={{ gridColumn: '1 / -1' }}
                        label={row.depends_on ? 'Dependent options by parent value' : 'Optional grouped options'}
                        helperText={row.depends_on
                          ? 'One rule per line. Example: Hindu: Brahmin, Rajput'
                          : 'Optional. One group per line if you want future dependent mapping.'}
                        value={groupedOptionsDrafts[row.field_key] ?? groupedOptionsText(row)}
                        onChange={(e) => setGroupedOptionsDrafts((previous) => ({ ...previous, [row.field_key]: e.target.value }))}
                        onBlur={(e) => updateRow(index, { grouped_options: parseGroupedOptions(e.target.value) })}
                      />
                    )}
                  </Box>
                )}
              </CardContent>
            </Card>
          ))}

          <Stack direction="row" spacing={1} justifyContent="space-between">
            <Button variant="outlined" startIcon={<AddIcon />} onClick={addField}>
              Add Custom Field
            </Button>
            <Button variant="contained" onClick={saveConfig} disabled={saving}>
              {saving ? 'Saving...' : 'Save Configuration'}
            </Button>
          </Stack>
        </Stack>
      )}
    </AdminLayout>
  );
};

export default AdminRegistrationFieldsPage;
